// Shared trip-state kernel — the pure list/map transforms that BOTH web
// (web/src/lib/trip-state.ts) and mobile (mobile/lib/trip-ops.ts) build on,
// plus stable id generation. Dual-ported: a logic-identical copy lives at
// mobile/lib/trip-state-core.ts — keep the two in lockstep. This file is
// import-free and generic on purpose, so the two copies can stay
// byte-identical across the web (Vite) and mobile (Metro) builds.
//
// Why this exists: the Worker validates NOTHING on PUT /state (handlePut
// only enforces If-Match → 412), so these transforms ARE the wire
// contract. Before this module the booking/slot/saved/reservation
// transforms lived in three hand-maintained copies (web trip-state.ts,
// the mobile trip-ops.ts reducer, and a now-deleted trip-mutations.ts
// write path) that could silently disagree. Centralizing the append /
// remove / merge-patch / saved / reservation behavior here means a new
// field or a behavior tweak lands once.
//
// Every helper operates on the BARE collection (the array or map), not on
// the whole TripState — each surface keeps its own TripState shape and
// spreads the result back in. Helpers return the SAME reference they were
// passed when nothing changed, so callers can `next === input` to skip a
// no-op write.

// Stable id: base36 timestamp + 6 random base36 chars. Matches the scheme
// every surface used before this module existed, so an optimistic insert
// and the eventual server row share an id.
export function genId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Spread `patch`'s DEFINED (!== undefined) keys over a shallow clone of
// `base`. Field-agnostic — a new booking/slot field flows through without
// editing this function. (Pass `null` explicitly to clear a field.)
export function mergeDefined<T>(base: T, patch: Partial<T>): T {
  const next = { ...base };
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const v = patch[key];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (v !== undefined) (next as any)[key] = v;
  }
  return next;
}

// ─── id-keyed record lists (bookings, scraps, notes, todos, …) ───────

// Append `record` unless a row with the same id already exists. The
// id-guard makes mobile's offline drain replay-safe (apply the same op
// twice → one row); web passes a freshly-genId'd record so it always
// appends. Returns the same array reference when the id already exists.
export function appendById<T extends { id: string }>(
  list: T[] | undefined,
  record: T,
): T[] {
  const arr = list ?? [];
  return arr.some((x) => x.id === record.id) ? arr : [...arr, record];
}

// Remove the row with `id`. Returns the same array reference when nothing
// matched, so callers can `next === input` to skip a no-op write.
export function removeById<T extends { id: string }>(
  list: T[] | undefined,
  id: string,
): T[] {
  const arr = list ?? [];
  const next = arr.filter((x) => x.id !== id);
  return next.length === arr.length ? arr : next;
}

// Merge `patch`'s defined keys into the row with `id`. Returns the same
// array reference when no row matched.
export function patchById<T extends { id: string }>(
  list: T[] | undefined,
  id: string,
  patch: Partial<T>,
): T[] {
  const arr = list ?? [];
  let touched = false;
  const next = arr.map((row) => {
    if (row.id !== id) return row;
    touched = true;
    return mergeDefined(row, patch);
  });
  return touched ? next : arr;
}

// ─── Slots (itinerary[date].slots, keyed by slotId) ──────────────────

type SlotMap<S extends { slotId: string }> = Record<string, { slots: S[] }>;

// Append a slot to itinerary[dateISO].slots, creating the day if absent.
// Returns the same map reference when a slot with that slotId already
// exists (replay-safe).
export function appendSlot<S extends { slotId: string }>(
  itinerary: SlotMap<S> | undefined,
  dateISO: string,
  slot: S,
): SlotMap<S> {
  const itin = itinerary ?? {};
  const day = itin[dateISO] ?? { slots: [] };
  if (day.slots.some((s) => s.slotId === slot.slotId)) return itin;
  return { ...itin, [dateISO]: { ...day, slots: [...day.slots, slot] } };
}

// Remove a slot by slotId. Returns the same map reference when the day or
// the slot wasn't found.
export function removeSlot<S extends { slotId: string }>(
  itinerary: SlotMap<S> | undefined,
  dateISO: string,
  slotId: string,
): SlotMap<S> {
  const itin = itinerary ?? {};
  const day = itin[dateISO];
  if (!day) return itin;
  const next = day.slots.filter((s) => s.slotId !== slotId);
  return next.length === day.slots.length
    ? itin
    : { ...itin, [dateISO]: { ...day, slots: next } };
}

// ─── Saved items + notes ─────────────────────────────────────────────

// Toggle membership of `id` in savedItems[category] (add if absent,
// remove if present). Always returns a new map (membership always flips).
export function toggleSaved(
  savedItems: Record<string, string[]> | undefined,
  category: string,
  id: string,
): Record<string, string[]> {
  const current = savedItems ?? {};
  const list = Array.isArray(current[category]) ? current[category] : [];
  const idx = list.indexOf(id);
  const nextList =
    idx >= 0 ? [...list.slice(0, idx), ...list.slice(idx + 1)] : [...list, id];
  return { ...current, [category]: nextList };
}

// Set membership of `id` to exactly `on`. Returns the same map reference
// when already in the desired state.
export function setSaved(
  savedItems: Record<string, string[]> | undefined,
  category: string,
  id: string,
  on: boolean,
): Record<string, string[]> {
  const current = savedItems ?? {};
  const list = Array.isArray(current[category]) ? current[category] : [];
  const has = list.indexOf(id) >= 0;
  if (on === has) return current;
  const nextList = on ? [...list, id] : list.filter((x) => x !== id);
  return { ...current, [category]: nextList };
}

// Set or clear one saved-item note (keyed `${category}:${id}`). An empty
// (trimmed) note deletes the key. Returns the same map reference on no-op.
export function setSavedNote(
  savedNotes: Record<string, string> | undefined,
  key: string,
  note: string,
): Record<string, string> {
  const current = savedNotes ?? {};
  const trimmed = note.trim();
  if (!trimmed) {
    if (!(key in current)) return current;
    const { [key]: _drop, ...rest } = current;
    return rest;
  }
  if (current[key] === trimmed) return current;
  return { ...current, [key]: trimmed };
}

// ─── Reservations (keyed by refKind + refId) ─────────────────────────

// Append a reservation unless one with the same (refKind, refId) already
// exists. Returns the same array reference on a duplicate.
export function appendReservation<T extends { refKind: string; refId: string }>(
  list: T[] | undefined,
  record: T,
): T[] {
  const arr = list ?? [];
  return arr.some((r) => r.refKind === record.refKind && r.refId === record.refId)
    ? arr
    : [...arr, record];
}

// Remove the reservation matching (refKind, refId). Returns the same array
// reference when nothing matched.
export function removeReservation<T extends { refKind: string; refId: string }>(
  list: T[] | undefined,
  refKind: string,
  refId: string,
): T[] {
  const arr = list ?? [];
  const next = arr.filter((r) => !(r.refKind === refKind && r.refId === refId));
  return next.length === arr.length ? arr : next;
}

// Drop entries no screen can render: a non-object in `bookings`, in a
// day's `slots`, or as an itinerary day (null, a string, a number). The
// Worker stores whatever a writer PUTs, and one `null` in `bookings`
// crashed the mobile trip home (every reader does `b.kind`). A state with
// no `tripMeta` object (written by the API or MCP without one) crashed
// the day screen, so that becomes {}. Run on the state as it is read
// (fetch + cache), so reducers and screens only see objects.
// Wrong-typed FIELDS are left alone — tolerant readers handle those.
// Returns the same reference when nothing changed.
export function sanitizeTripState<S>(state: S): S {
  if (!state || typeof state !== 'object') return state;
  const s = state as Record<string, unknown>;
  const isObj = (v: unknown) => !!v && typeof v === 'object' && !Array.isArray(v);
  let out: Record<string, unknown> | null = isObj(s.tripMeta) ? null : { ...s, tripMeta: {} };
  if (Array.isArray(s.bookings) && !s.bookings.every(isObj)) {
    out = { ...(out ?? s), bookings: s.bookings.filter(isObj) };
  }
  const itin = s.itinerary;
  if (isObj(itin)) {
    let nextItin: Record<string, unknown> | null = null;
    for (const [date, day] of Object.entries(itin as Record<string, unknown>)) {
      if (!isObj(day)) {
        nextItin = nextItin ?? { ...(itin as Record<string, unknown>) };
        delete nextItin[date];
        continue;
      }
      const slots = (day as Record<string, unknown>).slots;
      if (slots !== undefined && !(Array.isArray(slots) && slots.every(isObj))) {
        nextItin = nextItin ?? { ...(itin as Record<string, unknown>) };
        nextItin[date] = {
          ...(day as Record<string, unknown>),
          slots: Array.isArray(slots) ? slots.filter(isObj) : [],
        };
      }
    }
    if (nextItin) out = { ...(out ?? s), itinerary: nextItin };
  }
  return (out ?? state) as S;
}

// The fields of `next` that differ from `before`, for an edit form's save.
// A form that sends every field overwrites edits another writer (web, MCP,
// a partner's phone) made while the form was open; sending only what the
// user changed lets those survive. Missing / undefined / '' / [] on either
// side all compare as null, so a blank field that was never set isn't sent.
// Values compare by JSON (arrays like `members` included).
export function changedFields<T extends object>(
  next: T,
  before: Partial<Record<keyof T, unknown>> | null | undefined,
): Partial<T> {
  const norm = (v: unknown) =>
    v === undefined || v === '' || (Array.isArray(v) && v.length === 0) ? null : v;
  const out: Partial<T> = {};
  for (const k of Object.keys(next) as Array<keyof T>) {
    const a = norm(next[k]);
    const b = norm(before ? before[k] : undefined);
    if (JSON.stringify(a) !== JSON.stringify(b)) out[k] = next[k];
  }
  return out;
}
